import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { PartnerFileUrls } from '@/api/partner'

/** Просмотр PDF в браузере: iframe на подписанную ссылку, кнопки «Скачать»
 *  и «В новой вкладке». Без Modal: ему узко под документ. Используется в
 *  списке заявок (иконки документов) и в карточке лида («Документы»). */
export function PdfViewer({ title, urls, onClose }: { title: string; urls: PartnerFileUrls; onClose: () => void }) {
  const { t } = useTranslation()
  // Escape закрывает просмотрщик, как и клик по подложке.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--stryzh-overlay-scrim-modal)] p-2 sm:p-4"
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    >
      <div
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="stryzh-panel flex h-[94vh] w-full max-w-[1100px] flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <h2 className="truncate font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {title}
          </h2>
          <div className="flex shrink-0 items-center gap-2">
            <a href={urls.download} className="stryzh-btn stryzh-btn--primary inline-flex items-center gap-2">
              <Download size={16} aria-hidden />
              {t('partner.viewer.download')}
            </a>
            <a
              href={urls.view}
              target="_blank"
              rel="noreferrer"
              className="stryzh-btn stryzh-btn--secondary inline-flex items-center gap-2"
              title={t('partner.viewer.newTab')}
            >
              <ExternalLink size={16} aria-hidden />
              <span className="hidden sm:inline">{t('partner.viewer.newTab')}</span>
            </a>
            <Button variant="ghost" onClick={onClose} aria-label={t('common.close')} className="stryzh-btn--close-icon">
              ✕
            </Button>
          </div>
        </div>
        <iframe src={urls.view} title={title} className="h-full w-full flex-1 border-0 bg-white" />
      </div>
    </div>
  )
}

/** Состояние открытого просмотрщика: заголовок и ссылки. */
export interface PdfViewerState {
  title: string
  urls: PartnerFileUrls
}
